import type { SimulationEvent } from "./types";

export class AudioEngine {
  private context: AudioContext | null = null;
  private enabled = true;

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled && this.context?.state === "running") void this.context.suspend();
  }

  unlock(): void {
    if (!this.enabled) return;
    this.context ??= new AudioContext();
    if (this.context.state === "suspended") void this.context.resume();
  }

  playUi(): void {
    this.tone(440, 0.035, 0.025, "sine", 0);
  }

  playCountdown(value: number): void {
    this.tone(value <= 1 ? 720 : 480, value <= 1 ? 0.16 : 0.08, 0.06, "square", 0);
  }

  playEvent(event: SimulationEvent): void {
    if (event.type === "impact") {
      this.tone(95 + event.strength * 120, 0.055, 0.04 + event.strength * 0.055, "triangle", event.pan);
      return;
    }
    if (event.type === "wall") {
      this.tone(180 + event.strength * 80, 0.035, 0.018 + event.strength * 0.025, "square", event.pan);
      return;
    }
    if (event.type === "goal") {
      this.chord(event.scorer === "local" ? [330, 495, 660] : [220, 196, 174], 0.3);
      return;
    }
    if (event.type === "finished") {
      this.chord(event.winner === "local" ? [392, 494, 587, 784] : [247, 220, 196], 0.42);
    }
  }

  dispose(): void {
    if (this.context) void this.context.close();
    this.context = null;
  }

  private chord(frequencies: readonly number[], duration: number): void {
    frequencies.forEach((frequency, index) => {
      window.setTimeout(() => this.tone(frequency, duration, 0.045, "sine", 0), index * 55);
    });
  }

  private tone(frequency: number, duration: number, volume: number, type: OscillatorType, pan: number): void {
    if (!this.enabled || !this.context || this.context.state !== "running") return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    const panner = this.context.createStereoPanner();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, frequency * 0.72), now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    panner.pan.value = Math.min(1, Math.max(-1, pan));
    oscillator.connect(gain).connect(panner).connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }
}
